/**
 * A signed copy: who answered what, against which version, and the
 * statement they attested to. Immutable — this is the compliance record.
 */

import { useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { formatDay } from '../../shared/dates';
import { useSubmission } from './useForms';
import { answerText, FIELD_TYPE_LABEL } from './types';
import styles from '../../shared/Page.module.css';
import own from './Forms.module.css';

export function SubmissionPage() {
  const { submissionId = '' } = useParams();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const sub = useSubmission(orgId, submissionId);

  if (sub.isLoading) return <Spinner label="Opening the receipt" />;
  if (sub.error || !sub.data) {
    return (
      <div className={styles.body}>
        <PageState title="That receipt is not here" action={{ to: '/forms/mine', label: 'Back to your forms' }} tone="alert">
          Only the person who signed, and administrators, can open a receipt.
        </PageState>
      </div>
    );
  }
  const s = sub.data;
  const when = s.submittedAt ? new Date(s.submittedAt) : null;

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          <p className={styles.eyebrow}>Receipt · form version {s.formVersionNumber}</p>
          <div className={styles.titleLine}>
            <BackLink to="/forms/mine" label="Back to your forms" />
            <h1 className={styles.title}>Signed by {s.userDisplayName ?? s.userEmail ?? 'a member'}</h1>
          </div>
          <p className={styles.subtitle}>
            {when ? `${formatDay(s.submittedAt)} at ${when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : '—'}
            {s.userEmail && ` · ${s.userEmail}`}. Kept exactly as answered; it cannot be changed.
          </p>
        </div>
      </header>
      <div className={styles.body}>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Answers</h2>
          <dl className={own.receipt}>
            {s.responses.map((r) => (
              <li key={r.fieldId}>
                <dt>{r.labelSnapshot}<span className={own.sub}>{FIELD_TYPE_LABEL[r.type]}</span></dt>
                <dd className={r.type === 'signature' ? own.signature : undefined}>
                  {r.type === 'fileUpload' && typeof r.value === 'string' && r.value
                    ? <a href={r.value} target="_blank" rel="noreferrer">Open file</a>
                    : answerText(r.type, r.value)}
                </dd>
              </li>
            ))}
          </dl>
        </section>
        {s.legalAttestationTextSnapshot && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Attested</h2>
            <p className={own.statement}>{s.legalAttestationTextSnapshot}</p>
          </section>
        )}
      </div>
    </>
  );
}
